import { CalendarEvent, CalendarEventType } from '../../../types';
import { getEffectiveDuration, timeToRowNumber } from '../../../utils';

export const OVERLAP_EVENT_MIN_CARD_WIDTH = 68;
const OVERLAP_EVENT_GAP = 4;
export const OVERLAP_EVENTS_BUTTON_WIDTH = 40;
const OVERLAP_EVENTS_BUTTON_TOTAL_SPACE = OVERLAP_EVENTS_BUTTON_WIDTH + OVERLAP_EVENT_GAP;

export const CALENDAR_GRID_OVERLAP_EVENT_TYPES = ['exception', 'scheduleRequest', 'appointment'] as const;

export const OVERLAP_EVENT_PRIORITY: readonly CalendarEventType[] = CALENDAR_GRID_OVERLAP_EVENT_TYPES;

export const isCalendarGridOverlapEventType = (eventType: CalendarEventType) => GROUPABLE_EVENT_TYPES.has(eventType);

const OVERLAP_EVENT_PRIORITY_MAP = new Map(OVERLAP_EVENT_PRIORITY.map((eventType, index) => [eventType, index]));

const GROUPABLE_EVENT_TYPES = new Set<CalendarEventType>(CALENDAR_GRID_OVERLAP_EVENT_TYPES);

type LayoutEvent = CalendarEvent & {
  startRow: number;
  endRow: number;
  priority: number;
};

type LayoutLane = {
  priority: number;
  events: LayoutEvent[];
};

export type OverlapEventGroup = {
  lanes: LayoutLane[];
  laneCount: number;
};

const getPriority = (eventType: CalendarEventType) =>
  OVERLAP_EVENT_PRIORITY_MAP.get(eventType) ?? OVERLAP_EVENT_PRIORITY.length;

const toLayoutEvent = (event: CalendarEvent): LayoutEvent => {
  const startRow = timeToRowNumber(event.startTime);
  const duration = getEffectiveDuration(event.duration);

  return {
    ...event,
    startRow,
    endRow: startRow + duration,
    priority: getPriority(event.eventType),
  };
};

const overlaps = (eventA: LayoutEvent, eventB: LayoutEvent) =>
  eventA.startRow < eventB.endRow && eventB.startRow < eventA.endRow;

const groupByOverlap = (events: LayoutEvent[]) => {
  const sortedEvents = [...events].sort((eventA, eventB) => {
    if (eventA.startRow !== eventB.startRow) return eventA.startRow - eventB.startRow;
    if (eventA.priority !== eventB.priority) return eventA.priority - eventB.priority;
    if (eventA.endRow !== eventB.endRow) return eventB.endRow - eventA.endRow;
    return eventA.id.localeCompare(eventB.id);
  });

  const groups: LayoutEvent[][] = [];
  let activeGroup: LayoutEvent[] = [];
  let activeGroupEnd = 0;

  for (const event of sortedEvents) {
    if (!activeGroup.length) {
      activeGroup = [event];
      activeGroupEnd = event.endRow;
      continue;
    }

    if (event.startRow < activeGroupEnd) {
      activeGroup.push(event);
      activeGroupEnd = Math.max(activeGroupEnd, event.endRow);
      continue;
    }

    groups.push(activeGroup);
    activeGroup = [event];
    activeGroupEnd = event.endRow;
  }

  if (activeGroup.length) {
    groups.push(activeGroup);
  }

  return groups;
};

const putGroupEventsIntoLanes = (groupEvents: LayoutEvent[]) => {
  const lanes: LayoutLane[] = [];

  for (const event of groupEvents) {
    let inserted = false;

    for (const lane of lanes) {
      const hasOverlap = lane.events.some((laneEvent) => overlaps(laneEvent, event));
      if (!hasOverlap) {
        lane.events.push(event);
        lane.priority = Math.min(lane.priority, event.priority);
        inserted = true;
        break;
      }
    }

    if (!inserted) {
      lanes.push({ priority: event.priority, events: [event] });
    }
  }

  return lanes.sort((laneA, laneB) => laneA.priority - laneB.priority || laneB.events.length - laneA.events.length);
};

export const getOverlappingEventGroups = (events: CalendarEvent[]) => {
  const groupableEvents = events.filter((event) => GROUPABLE_EVENT_TYPES.has(event.eventType)).map(toLayoutEvent);
  const groups = groupByOverlap(groupableEvents);

  return groups.map((groupEvents) => {
    const lanes = putGroupEventsIntoLanes(groupEvents);

    return {
      lanes,
      laneCount: lanes.length,
    } satisfies OverlapEventGroup;
  });
};

export const getVisibleOverlapLaneCount = (columnWidth: number, laneCount: number) => {
  if (laneCount <= 0) return 0;

  const laneWidth = OVERLAP_EVENT_MIN_CARD_WIDTH + OVERLAP_EVENT_GAP;
  const visibleWithoutOverflow = Math.floor((columnWidth + OVERLAP_EVENT_GAP) / laneWidth);
  if (laneCount <= visibleWithoutOverflow) {
    return Math.max(1, Math.min(laneCount, visibleWithoutOverflow));
  }

  const visibleWithOverflow = Math.floor(
    (columnWidth - OVERLAP_EVENTS_BUTTON_TOTAL_SPACE + OVERLAP_EVENT_GAP) / laneWidth
  );
  return Math.max(1, Math.min(laneCount, visibleWithOverflow));
};
